/**
 * CodeBlock — `pre` renderer custom pour `react-markdown` variant 'document'.
 *
 * @remarks
 * Route le bloc de code selon son language :
 * - `mermaid` ou `mermaidjs` (insensible à la casse) → `<MermaidBlock>` lazy
 *   (mermaid lib ~600 KB chargée à la première occurrence d'un diagramme)
 * - autres → `<HighlightedBlock>` avec syntax highlighting Prism (oneDark)
 *
 * Extraction du `language` et du `codeText` via parsing des children
 * react-markdown : le `<pre>` wrap un `<code>` avec `className="language-X"`.
 */

import { Children, isValidElement, lazy, Suspense, type ReactNode } from 'react';
import { LoadingState } from '../screen-state';
import { HighlightedBlock } from './HighlightedBlock';

const MermaidBlock = lazy(() =>
  import('./MermaidBlock').then((m) => ({ default: m.MermaidBlock })),
);

/** Fence names rendered as a diagram — `mermaidjs` is common in docs and LLM output. */
const MERMAID_LANGUAGES: ReadonlySet<string> = new Set(['mermaid', 'mermaidjs']);

interface CodeBlockProps {
  children?: ReactNode;
}

export function CodeBlock({ children }: CodeBlockProps) {
  const { language, codeText } = parseCodeChildren(children);

  if (MERMAID_LANGUAGES.has(language.toLowerCase())) {
    return (
      <Suspense
        fallback={
          <LoadingState
            inline
            label="Chargement du diagramme…"
            className="bg-bg-subtle my-3 rounded-md p-3 text-xs"
          />
        }
      >
        <MermaidBlock code={codeText} />
      </Suspense>
    );
  }

  // Bloc de contenu : les numéros de ligne suivent le réglage de l'utilisateur.
  return <HighlightedBlock language={language} codeText={codeText} lineNumbers="preference" />;
}

interface ParsedCode {
  language: string;
  codeText: string;
}

function parseCodeChildren(children: ReactNode): ParsedCode {
  let language = '';
  let codeText = '';

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if ((child as React.ReactElement).type !== 'code') return;
    const props = (child as React.ReactElement).props as {
      className?: string;
      children?: ReactNode;
    };
    const className = props.className ?? '';
    const match = /language-(\w+)/.exec(className);
    if (match?.[1]) language = match[1];
    codeText = extractText(props.children).replace(/\n$/, '');
  });

  return { language, codeText };
}

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (isValidElement(node)) {
    return extractText((node.props as { children?: ReactNode }).children);
  }
  return '';
}
